import React from 'react';

import { Button } from '@/design-system/button';
import { CellConflict, Zone } from '@/services/api/zones';

interface ConflictBannerProps {
  /** When non-empty, the user attempted to add cells that already belong elsewhere. */
  conflicts: CellConflict[];
  /** All known zones (for friendly labels — keyed by id). */
  zonesById: Map<string, Zone>;
  /**
   * Reassign moves every conflicting cell from its current zone into the
   * active zone. The parent does the API calls; the banner only emits the
   * decision.
   */
  onReassign: () => void;
  onDismiss: () => void;
  isWorking: boolean;
}

/**
 * Floating banner anchored to the top-center of the map. Appears only
 * when there's a cell conflict to resolve and disappears on reassign or
 * cancel, so it doesn't permanently eat real estate.
 */
const ConflictBanner: React.FC<ConflictBannerProps> = ({
  conflicts,
  zonesById,
  onReassign,
  onDismiss,
  isWorking,
}) => {
  if (conflicts.length === 0) return null;

  // Group conflicts by current owner.
  const groups = new Map<string, CellConflict[]>();
  for (const c of conflicts) {
    const list = groups.get(c.existingZone.id) ?? [];
    list.push(c);
    groups.set(c.existingZone.id, list);
  }
  const groupEntries = Array.from(groups.entries());

  // Single owner → name it; multiple → summary count.
  const ownerLabel = (() => {
    if (groupEntries.length === 1) {
      const [zoneId, list] = groupEntries[0];
      const z = zonesById.get(zoneId);
      return z
        ? `${z.code}${z.name ? ` (${z.name})` : ''}`
        : list[0].existingZone.code;
    }
    return `${groupEntries.length} other zones`;
  })();

  return (
    <div className="flex max-w-[520px] items-center gap-3 rounded-2xl border border-error-light bg-error-lighter px-4 py-2.5 shadow-sm">
      <i className="ri-error-warning-line text-[18px] text-error-base" />
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-semibold tracking-tight text-text-strong-950">
          {conflicts.length} cell{conflicts.length === 1 ? '' : 's'} claimed by{' '}
          {ownerLabel}
        </p>
        <p className="truncate text-[10.5px] text-text-sub-600">
          Reassign moves {conflicts.length === 1 ? 'it' : 'them'} into the
          active zone.
        </p>
      </div>
      <Button
        tone="destructive"
        size="sm"
        onClick={onReassign}
        disabled={isWorking}
        loading={isWorking}
      >
        {isWorking ? 'Reassigning…' : 'Reassign'}
      </Button>
      <Button
        tone="neutral"
        style="stroke"
        size="sm"
        onClick={onDismiss}
        disabled={isWorking}
      >
        Cancel
      </Button>
    </div>
  );
};

export default ConflictBanner;
